// Generic localStorage-backed async CRUD store shared by every Assessment
// content module (hierarchy, questions, videos, tests). Every read/write in
// this file already goes through a Promise, so when the backend is ready,
// swapping the bodies of getAll/getById/save/remove below for real
// `api.get/post/put/delete(...)` calls is the ONLY change needed — no
// component that consumes a store has to change, since they already
// await/.then() these calls and manage their own loading state.

// Tune this to make the demo feel like it's talking to a real network, or
// set to 0 for an instant local demo.
const SIMULATED_DELAY_MS = 250;

const delay = (value) =>
  new Promise((resolve) => setTimeout(() => resolve(value), SIMULATED_DELAY_MS));

function readAll(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return fallback;
}

function writeAll(key, rows) {
  try {
    localStorage.setItem(key, JSON.stringify(rows));
  } catch {}
}

// One cached array per store, hydrated from localStorage on first access so
// repeated getAll() calls in the same session don't re-parse JSON every time.
export function createStore(key, defaults) {
  let cache = null;
  const sync = () => {
    if (!cache) cache = readAll(key, defaults);
    return cache;
  };
  const persist = (rows) => {
    cache = rows;
    writeAll(key, rows);
  };

  return {
    sync,
    async getAll() {
      return delay([...sync()]);
    },
    async getById(id) {
      return delay(sync().find((r) => r.id === id) || null);
    },
    async save(row, previousId) {
      const rows = sync();
      const idx = rows.findIndex((r) => r.id === (previousId || row.id));
      if (idx >= 0) rows[idx] = row;
      else rows.push(row);
      persist(rows);
      return delay(row);
    },
    async remove(id) {
      persist(sync().filter((r) => r.id !== id));
      return delay(true);
    },
    async removeWhere(predicate) {
      const removed = sync().filter(predicate);
      persist(sync().filter((r) => !predicate(r)));
      return delay(removed);
    },
  };
}

// Build once, use many times — avoids repeated O(n) .find()/.filter() scans
// when resolving parent/child chains for a whole table's worth of rows.
export function groupBy(rows, key) {
  const map = new Map();
  rows.forEach((r) => {
    const list = map.get(r[key]);
    if (list) list.push(r);
    else map.set(r[key], [r]);
  });
  return map;
}

export function indexBy(rows, key = "id") {
  const map = new Map();
  rows.forEach((r) => map.set(r[key], r));
  return map;
}

export const slugify = (value) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const STATUS_OPTIONS = ["ACTIVE", "INACTIVE"];
